import type { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Color } from "@skillscratch/shared-constants";
import type { CSSProperties, MouseEventHandler, ReactNode } from "react";
import classes from "./Button.module.scss";

export interface ButtonProps {
  text?: string;
  icon?: IconDefinition;
  foregroundColor?: string;
  background?: string;
  fillWidth?: boolean;
  buttonStyle?: "filled" | "borderless";
  disabled?: boolean;
  style?: CSSProperties;
  compact?: boolean;
  className?: string; // Added className prop

  children?: ReactNode;
  onClick: MouseEventHandler<HTMLButtonElement>;
  onPointerDown?: MouseEventHandler<HTMLButtonElement>;
}

export function Button({
  text,
  icon,
  foregroundColor,
  background = Color.blue,
  fillWidth = false,
  buttonStyle = "filled",
  disabled = false,
  style,
  compact,
  className = "", // Added className with default empty string
  children,
  onClick,
  onPointerDown,
}: ButtonProps) {
  return (
    // biome-ignore lint/a11y/useButtonType: <explanation>
    <button
      className={`${
        buttonStyle === "filled"
          ? classes["button-filled"]
          : classes["button-borderless"]
      } ${className}`}
      onClick={onClick}
      onPointerDown={onPointerDown}
      style={{
        display: "block",
        boxSizing: "border-box",
        padding: compact ? "0.4em 0.57em" : "0.67em 0.86em",
        borderRadius: "0.3em",
        margin: "0 auto",
        fontSize: compact ? "1.2em" : "1.625em",
        textAlign: "center",
        lineHeight: "1",

        ...(buttonStyle === "borderless"
          ? {
              color:
                foregroundColor ??
                (`rgb(from ${Color.darkBlue} r g b / 0.4)`),
              background: undefined,
            }
          : {
              color: foregroundColor ?? "#fff",
              background,
            }),
        ...(fillWidth ? { width: "100%" } : {}),
        ...(disabled ? { opacity: 0.5 } : {}),
        ...style,
      }}
      disabled={disabled}
    >
      {(text || icon) && (
        <div>
          <span className='font-semibold'>{text}</span>
          {icon && (
            <FontAwesomeIcon
              icon={icon}
              style={{
                display: "inline-block",
                marginInlineStart: "0.5em",
              }}
            />
          )}
        </div>
      )}
      {children}
    </button>
  );
}